import { z } from "zod";
/**
 * Who is in a workspace, which brands each person sees, and what they may
 * change (DESIGN.md, "People and access").
 *
 * Agreed 2026-10-05. Three roles: an owner and an admin can do everything, and
 * an admin cannot change the owners; a member sees the brands they were given
 * and changes only what was switched on for them. Seeing is never a switch —
 * everyone reads everything on their own brands.
 *
 * The Rust API answers in exactly these shapes (`server/crates/api/src/modules/
 * members`): `GET /people`, `GET /people/me`, `POST /people/invites`,
 * `PUT /people/{userId}/access`.
 */
export const accessRoleSchema = z.enum(["owner", "admin", "member"]);
/**
 * "All" includes brands added later. A chosen list never grows by itself,
 * except by a brand that person adds, which joins their list. A stored list can
 * be empty — its brands were deleted since — so only a saved one needs one.
 */
export const brandAccessSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("all") }),
    z.object({ kind: z.literal("some"), brandIds: z.array(z.string().min(1)) }),
]);
/**
 * The switches a member can be given. Meaningless for an owner or admin, who
 * hold all of them; the server stores them only for a member.
 *
 * Deleting a question from the library is deliberately absent: one question can
 * feed several brands, so it needs "All brands" plus `questions`, not a switch.
 */
export const abilitiesSchema = z.object({
    addBrands: z.boolean(),
    questions: z.boolean(),
    editBrands: z.boolean(),
    pauseTracking: z.boolean(),
});
export const ABILITY_KEYS = [
    "addBrands",
    "questions",
    "editBrands",
    "pauseTracking",
];
export const accessSchema = z.object({
    role: accessRoleSchema,
    brands: brandAccessSchema,
    abilities: abilitiesSchema,
});
export const personSchema = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
    joinedAt: z.string(),
    isYou: z.boolean(),
    access: accessSchema,
});
/** An invite still waiting. Its access is applied the moment it is accepted. */
export const accessInviteSchema = z.object({
    id: z.string(),
    email: z.string(),
    createdAt: z.string(),
    expiresAt: z.string(),
    invitedBy: z.string().nullable(),
    access: accessSchema,
});
/** A brand someone can be given: the subject of a report. */
export const brandOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
    primaryDomain: z.string(),
});
export const peopleListSchema = z.object({
    people: z.array(personSchema),
    invites: z.array(accessInviteSchema),
    brands: z.array(brandOptionSchema),
    /** Whether the person asking may invite, remove and change access. */
    canManage: z.boolean(),
    /** Whether they may also make or unmake owners: only an owner can. */
    canManageOwners: z.boolean(),
});
export const inviteWithAccessSchema = z.object({
    email: z.email(),
    access: accessSchema,
});
/** The link exists here and nowhere else: only its hash is stored. */
export const createdAccessInviteSchema = z.object({
    invite: accessInviteSchema,
    link: z.string(),
    emailed: z.boolean(),
});
export const setAccessSchema = z.object({ access: accessSchema });
//# sourceMappingURL=access.js.map